<template>
  <div class="invoice-send-wrap">
    <portal to="dash-modal">
      <MollieNoProfileModal 
        v-if="showMollieModal && showMollieModal === 'no-profile'"
        @close="showMollieModal = false"
      />
      <MollieMissingDataModal 
        v-if="showMollieModal && showMollieModal === 'missing-data'"
        @close="showMollieModal = false"
      />
      <InvoiceSendAdjustModal 
        v-if="showAdjustModal"
        :invoice-id="invoice.id"
        :mark-as-sent="markedAsSent"
        @send-invoice="sendInvoice"
        @close="showAdjustModal = false"
      />
    </portal>
    <div class="invoice-wrap-bg">
    <div class="invoice-wrap">
      <LoadingSpinner v-if="loading || isSending" :display-text="(isSending) ? $gettext('Sending invoice'): ''" />
      <b-form v-if="!loading" @submit.prevent="adjustAndSend" @keydown.enter="adjustAndSend" class="invoice-send-form">
        <b-form-group 
          id="input-group-recipients"
          :label="$gettext('Recipients*')"
          label-for="input-recipients"
          label-class="gk-label-margin gk-modal-label"
        >
          <b-input-group>
            <EmailFormInput v-if="customerLoaded" :preselected="sendDetails.recipients" id="input-recipients" ref="email_form_input" />
          </b-input-group>

          <small tabindex="-1" id="input-group-recipients__BV_description_" class="form-text text-muted" style="font-size: 14px;">{{ $gettext('You can add multiple email addresses') }}</small>

          <b-form-checkbox
            id="checkbox-includeme"
            v-model="updateCC"
            @change="updateCCSave"
            name="checkbox-includeme"
          >
            <span v-if="objectIsInvoice">{{ $gettext('Send me a copy of this Invoice') }}</span>
            <span v-else>{{ $gettext('Send me a copy of this Quotation') }}</span>
          </b-form-checkbox>

        </b-form-group>

        <b-form-group
          id="input-group-subject"
          :label="$gettext('Subject*')"
          label-for="input-subject"
          label-class="gk-label-margin gk-modal-label"
        >
          <b-form-input v-if="currentCompany.is_premium" id="input-subject" v-model="sendDetails.subject" />
          <b-form-input v-else id="input-subject" v-model="sendDetails.subject" readonly @click="$store.commit('togglePremiumModal', 'web')" />
        </b-form-group>

        <b-form-group id="input-group-message" label-class="gk-label-margin gk-modal-label" label-for="input-message">
          <template slot="label">
            <span v-translate>Message</span>
          </template>
          <b-input-group class="mb-2">
            <div class="premium-textarea-wrapper">
              <b-form-textarea id="input-message" v-model="sendDetails.message" required :placeholder="$gettext('Dear...')" rows="6" no-resize />
              <div class="premium-info text-blue" v-if="sendDetails.attach_mollie_payment">
                <p>{{ $gettext('Pay this invoice directly using payment link') }} <font-awesome-icon class="mx-2" role="button" @click="detachMolliePayment" icon="fa-solid fa-xmark" /></p>
              </div>
              <div v-if="!currentCompany.is_premium" class="premium-info text-unassigned" id="non-premium-text" v-b-tooltip.hover.top="$gettext('You can remove this text by upgrading to Gekko Premium')">
                <p>
                  <span>{{ emailMessage }}</span>
                </p>
                <p v-translate>Gekko: the friendly and free online bookkeeping tool for small business owners, <span>http://getgekko.com</span></p>
              </div>
            </div>
          </b-input-group>
          <b-form-checkbox
            id="checkbox-paymentlink"
            v-if="!sendDetails.attach_mollie_payment"
            v-model="sendDetails.attach_mollie_payment"
            @change="attachMolliePayment"
            name="checkbox-paymentlink"
            :disabled="mollieDisabled"
          >
            {{ $gettext('Attach payment link') }}
            <font-awesome-icon id="tooltip-paymentlink" class="tooltip-icon tooltip-icon--question ml-1" icon="fa-regular fa-circle-question" />
            <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-paymentlink" triggers="hover" container="#report-container">
              <div class="tooltip-text" v-html="$gettext('<span style=\'color: white; font-weight: bold; text-decoration: underline;\'>Pay this invoice directly using payment link</span> text will be appended to your message with a link to your configured payments service.')" />
            </b-tooltip>
          </b-form-checkbox>

          <div v-if="currencyNotEuro" class="text-info" v-translate>You cannot send payment links for invoices that are not in Euro.</div>
          <div v-if="amountLTEZero" class="text-info" v-translate>You cannot send payment links for invoices with negative totals.</div>
          <div v-if="showMollieError" class="text-danger">{{ mollieAttachError }}</div>

        </b-form-group>

        <b-form-group id="input-group-3" label-class="gk-label-margin gk-modal-label" label-for="input-3">
          <template slot="label">
            <span v-translate>Attachments</span>
          </template>

          <div class="attachments-table-wrap mb-2">
            <b-table 
              :items="fullAttachmentsList"
              :fields="documentFields"
              class="attachments-table"
              :class="['gk-table gk-table--hover gk-table--responsive gk-table--headed gk-table--medium']"
              responsive
            >
              <template #cell(title)="row">
                <span v-if="row.item.key === 'mainpdf'">
                  {{ row.item.title }}
                  <a class="mx-2 text-blue" :href="Urls['invoices_pdf'](invoice.id)" title="Download attached invoice">
                    <font-awesome-icon icon="fa-solid fa-download" />
                  </a>
                </span>
                <span v-else>
                  {{ row.item.title }}
                </span>
              </template>
              <template #cell(total)="row">
                <span v-if="row.item.file_size">
                  {{ niceBytes(row.item.file_size, 1000, true) }}
                </span>
                <span v-else-if="row.item.key === 'mainpdf' || row.item.key === 'costsreport'">
                  {{ invoice.currency }} {{ row.item.total | formatNumber }}
                </span>
                <span v-else-if="row.item.key === 'hoursreport'">
                  {{ row.item.total }} {{ $gettext('hours') }}
                </span>
              </template>
              <template #cell(actions)="row">
                <GekkoButton 
                  :disabled="row.item.key === 'mainpdf'"
                  @click="removeDocumentFromList(row)"
                  icon="fa-solid fa-trash"
                  variant="link"
                  class="text-danger"
                />
              </template>
            </b-table>
          </div>

          <b-form-checkbox
            id="checkbox-ubl"
            v-show="!sendDetails.attach_ubl"
            v-model="sendDetails.attach_ubl"
            name="checkbox-ubl"
          >
            {{ $gettext('Send UBL with invoice') }}
            <font-awesome-icon id="tooltip-ubl" class="tooltip-icon tooltip-icon--question ml-1" icon="fa-regular fa-circle-question" />
            <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-ubl" triggers="hover" container="#report-container">
              <div class="tooltip-text">
                {{ $gettext('UBL (Universal Business Language) is a standard for invoices that is used across the world. It is a format used in many accountancy applications.') }}
              </div>
            </b-tooltip>
          </b-form-checkbox>

          <b-form-checkbox
            id="checkbox-attach_costs"
            v-if="objectIsInvoice && attachedCosts && attachedCosts.count > 0"
            v-show="!sendDetails.attach_costs"
            v-model="sendDetails.attach_costs"
            name="checkbox-attach_costs"
          >
            {{ $gettext('Attach the cost proofs of your imported cost[s]') }}
          </b-form-checkbox>

          <b-form-checkbox
            id="checkbox-attach_hours"
            v-if="objectIsInvoice && attachedHours && attachedHours > 0"
            v-show="!sendDetails.attach_hours"
            v-model="sendDetails.attach_hours"
            name="checkbox-attach_hours"
          >
            {{ $gettext('Attach report of imported hours (.pdf)') }}
          </b-form-checkbox>

          <div>
            <div v-show="showDocumentSelector">
              <DocumentSelector 
                ref="document_selector"
                show-file-uploader
                @documentSelected="documentSelected"
                @showFileUploader="displayFileUploader"
              />
            </div>
            <GekkoButton 
              v-if="!showDocumentSelector"
              @click="displayDocumentSelector"
              :text="$gettext('Add another file')"
              icon="fa-solid fa-plus"
              variant="link"
              class="px-0"
            />
          </div>
        </b-form-group>

        <b-form-row v-if="showFileUploader" class="modal-file-uploader-row">
          <b-col cols="12" lg="12" class="d-flex flex-column h-100">
            <LoadingSpinner v-if="uploadingFiles" :display-text="$gettext('Uploading files. This might take a while.')" />

            <div style="display: flex; justify-content: space-between; align-items: flex-end; gap: 1rem; margin-bottom: 1rem;">
              <div>
                {{ $gettext('Total file size:') }} <span :class="{ 'text-danger': maxSizeExceeded }">{{ niceBytes(totalFileSize, 1000, true) }}</span> / {{ niceBytes(maxTotalFileSize*1000*1000, 1000, true) }}
              </div>
              <GekkoButton 
                v-if="invoiceFiles && invoiceFiles.length > 0"
                @click="uploadAndAttach"
                :text="$gettext('Upload and attach')"
                icon="fa-solid fa-upload"
              />
            </div>
            
            <FileUploader
              ref="file-uploader"
              showsize
              :file-size-max="maxTotalFileSize"
              @updated-queued-files="updateInvoiceFiles"
            />

            <div v-if="maxSizeExceeded" class="text-danger" style="font-size: 14px; padding: 0.5rem; margin-top: 1rem;">
              {{ $gettextInterpolate('Total size of files (%{ size }) exceeds upload limit. Please make sure your uploads do not exceed %{ max } MB.', { size: niceBytes(totalFileSize, 1000, true), max: maxTotalFileSize }) }}
            </div>
            <div class="text-center mt-2">
              <GekkoButton 
                v-if="invoiceFiles && invoiceFiles.length > 0"
                @click="uploadAndAttach"
                :text="$gettext('Upload and attach')"
                icon="fa-solid fa-upload"
              />
            </div>
          </b-col>
        </b-form-row>

      </b-form>
    </div>
    </div>
  </div>

</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { niceBytes } from "@/utils/";

import invoices from "@/mixins/invoices";

import InvoiceRecurrentService from '@/store/services/invoice.recurrent.service.js';
import InvoiceService from '@/store/services/invoice.service.js';
import MolliePaymentService from '@/store/services/molliepayment.service.js';
import CompanySettingService from '@/store/services/companysetting.service.js';
import CustomerService from "@/store/services/customer.service.js";
import EmailMessageNotificationService from "@/store/services/email-message-notification.service.js";
import CostService from "@/store/services/cost.service.js";
import HourService from "@/store/services/hour.service.js";
import DocumentUploadService from "@/store/services/document.upload.service.js";

import TitleBar from "@/components/TitleBar";
import GekkoButton from "@/components/GekkoButton";
import EmailFormInput from "@/components/ui/EmailFormInput";
import FileUploader from "@/components/ui/FileUploader";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import DocumentSelector from "@/components/documents/DocumentSelector";
import MollieNoProfileModal from "@/components/invoices/ui/MollieNoProfileModal"
import MollieMissingDataModal from "@/components/invoices/ui/MollieMissingDataModal"
import InvoiceSendAdjustModal from "@/components/invoices/ui/InvoiceSendAdjustModal"

export default {
  name: "InvoiceSend",
  mixins: [invoices],
  components: {
    TitleBar,
    GekkoButton,
    EmailFormInput,
    FileUploader,
    LoadingSpinner,
    DocumentSelector,
    MollieNoProfileModal,
    MollieMissingDataModal,
    InvoiceSendAdjustModal
  },
  data () {
    return {
      objectIsInvoice: true,
      recurrentResponse: false,
      sendDetails: {
        recipients: '',
        subject: '',
        message: '',
        attach_costs: false,
        attach_hours: false,
        attach_ubl: false,
        attach_mollie_payment: false
      },
      loading: true,
      customerLoaded: false,
      updateCC: false,
      invoice: null,
      invoiceFiles: null,
      invoiceNumber: null,
      uploadingFiles: false,
      isSending: false,
      maxTotalFileSize: 10,
      today: null,
      showUpdate: null,
      mollieStatus: null,
      mollieAttachError: null,
      mollieDisabled: false,
      showMollieError: false,
      showMollieModal: false,
      showAdjustModal: false,
      amountLTEZero: false,
      currencyNotEuro: false,
      attachedCosts: null,
      attachedHours: null,
      showDocumentSelector: false,
      showFileUploader: false,
      mainInvoicePDF: {
        key: 'mainpdf',
        title: '',
        total: '',
        description: ''
      },
      invoiceUBL: {
        key: 'ublfile',
        title: this.$gettext('UBL File attached'),
        description: ''
      },
      attachedHoursReport: {
        key: 'hoursreport',
        title: this.$gettext('Report of attached hours (.pdf)'),
        total: '',
        description: ''
      },
      selectedDocuments: [],
      documentFields: [
        { key: "title", label: this.$gettext('Title'), class: 'tablecol-md' },
        { key: "total", label: this.$gettext('Total / Size'), class: 'tablecol-sm' },
        { key: "description", label: this.$gettext('Description'), class: 'tablecol-md' },
        { key: "actions", label: "", tdClass: "text-right" }
      ],
      markedAsSent: false
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      isPremium: 'getIsPremium',
      // invoice: "invoices/getInvoice", // TODO use store invoice
      storeSendDetails: 'invoices/getSendDetails'
    }),
    emailMessage(){
      var message = this.$gettext('This email has been sent to you via Gekko on behalf of %{ company_name }.');
      message = message.replace("%{ company_name }", this.currentCompany.name);
      return message;
    },
    sendText () {
      let text = this.$gettext('Send invoice')
      return text
    },
    recipientsFullString () {
      return (this.$refs.email_form_input) ? this.$refs.email_form_input.getFullString() : ''
    },
    fullAttachmentsList () {
      const fullList = [this.mainInvoicePDF].concat(this.selectedDocuments)

      if (this.sendDetails.attach_costs) {
        this.attachedCosts.results.forEach(cost => {
          const costReport = {
            key: 'costsreport',
            title: cost.number,
            total: cost.amount,
            description: cost.description
          }
          fullList.push(costReport)
        })
      }

      if (this.sendDetails.attach_hours) {
        fullList.push(this.attachedHoursReport)
      }

      if (this.sendDetails.attach_ubl) {
        fullList.push(this.invoiceUBL)
      }
      return fullList
    },
    maxSizeExceeded () {
      return (this.totalFileSize > (this.maxTotalFileSize*1000*1000))
    },
    totalFileSize () {
      let totalFileSize = 0
      if (this.invoiceFiles) {
        for (var i = 0; i < this.invoiceFiles.length; i++) {
          totalFileSize += this.invoiceFiles[i].size;
        }
      }
      return totalFileSize
    },
  },
  created () {
    this.setupData()
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  beforeDestroy () {
    this.sendDetails.recipients = this.recipientsFullString
    console.log('send beforeDestroy', this.sendDetails)
    this.setSendDetails(this.sendDetails)
  },
  methods: {
    ...mapMutations ({ 
      setSendDetails: "invoices/SET_INVOICE_SEND_DETAILS",
      setCompanySettings: "main/SET_CURRENT_COMPANY_SETTINGS",
      setInvoiceStatus: 'invoices/SET_INVOICE_STATUS',

    }),
    ...mapActions ({ 
      getNextInvoiceNumber: "invoices/getNextInvoiceNumber"
    }),
    close () {
      this.$emit('close')
      document.body.classList.remove('modal-on')
    },
    setupData () {
      console.log('send created setup', this.storeSendDetails)

      if (this.objectIsInvoice) {
        this.updateCC = this.currentCompany.companysettings.include_sender_in_cc
      } else {
        this.updateCC = this.currentCompany.companysettings.include_sender_in_cc_quotation
      }
      this.today = new Date().toString('yyyy-MM-dd')
      const invoiceId = this.$route.params['invoiceId']
      if (invoiceId && Number.isInteger(parseFloat(invoiceId))) {
        // get invoice data and set default email values
        // get recurrent information if any
        InvoiceRecurrentService.getRecurrentInformation(invoiceId).then((response) => {
          let invoice_id
          if (response.data.recurrent) {
            this.recurrentResponse = response.data
            invoice_id = response.data.number
          } else {
            invoice_id = invoiceId
          }
          this.getInvoiceData(invoice_id)
        })
      } else {
        console.log('invoiceId is NaN')
        this.$router.push({ name: 'Invoices' })
      }
    },
    getInvoiceData (invoice_id) {
      InvoiceService.retrieve(invoice_id).then((response) => {
        this.invoice = response.data
        this.sendDetails.invoiceId = invoice_id

        const isDefaultNumber = /^\d{4}-\d{6}$/.test(this.invoice.number)
        const backupNumber = this.invoice.number
        const currentYear = { 'year': new Date(this.today).getFullYear() }
        
        this.getNextInvoiceNumber(currentYear).then((response) => {
          const newInvoiceNumber = response.data
          if (isDefaultNumber && currentYear != new Date(this.invoice.invoice_date).getFullYear()) {
            this.invoiceNumber = newInvoiceNumber
            this.showUpdate = true
          } else {
            this.invoiceNumber = backupNumber
            this.showUpdate = false
          }
          this.loading = false
          this.$emit('display-buttons', 'send')
        })
        this.sendDetails.attach_mollie_payment = this.currentCompany.attach_mollie_payment

        const invoice_amount = Math.max(parseFloat(this.invoice.total_amount), parseFloat(this.invoice.currency_total_amount))
        this.amountLTEZero = invoice_amount <= 0;
        this.currencyNotEuro = this.invoice.currency != 'EUR';

        if (this.currencyNotEuro || this.amountLTEZero) {
          this.sendDetails.attach_mollie_payment = false
          this.mollieDisabled = true
        } else {
          MolliePaymentService.getConnectionStatus().then((response) => {
            if (response.status == 200) {
              this.mollieStatus = 'ok'
            } else {
              switch (response.status) {
                case 298:
                  this.mollieStatus = 'no-profile'
                  this.mollieAttachError = this.$gettext('Your Mollie profile could not be found.')
                  break;
                case 299:
                  this.mollieStatus = 'missing-data'
                  this.mollieAttachError = this.$gettext('Your Mollie profile is missing data.')
                  break;
              }
              this.sendDetails.attach_mollie_payment = false
              this.updateMollieOnCompanySettings()
            }
          }).catch((error) => {
            if(error.status == 499){
              this.mollieStatus = 'not-premium';
              this.sendDetails.attach_mollie_payment = false
              this.updateMollieOnCompanySettings()
            } else {
              this.mollieStatus = 'mollie-error';
              this.mollieAttachError = this.$gettext('Your Mollie connection is not working right now.')
            }
          })
        }

        this.mainInvoicePDF.title = this.$gettext('Invoice #') + ' ' + this.invoice.number
        this.mainInvoicePDF.total = invoice_amount
        this.mainInvoicePDF.description = this.$gettext('due on') + ' ' + this.invoice.due_date

        this.sendDetails.attach_ubl = this.currentCompany.companysettings.attach_ubl_to_invoiceid
        this.sendDetails.message = this.currentCompany.companysettings.default_invoice_email_message
        this.sendDetails.subject = this.invoice.number + ' ' + this.$gettext('from') + ' ' + this.currentCompany.name

        CustomerService.retrieve(this.invoice.recipient_customer).then((response) => {
          if (this.$route.params['emailMessageId']) {
            EmailMessageNotificationService.retrieve(this.$route.params['emailMessageId']).then((response) => {
              this.sendDetails.recipients = response.data.recipients
              this.sendDetails.message = response.data.message
              if (this.recurrentResponse && this.recurrentResponse.recurrent) {
                this.sendDetails.invoiceId = this.recurrentResponse.number
              }
              this.sendDetails.subject = response.data.subject
              this.sendDetails.attach_ubl = response.data.attach_ubl
              this.sendDetails.attach_costs = response.data.attach_costs
              this.sendDetails.attach_hours = response.data.attach_hours
              this.sendDetails.documents = response.data.documents
            })
          } else {
            if (this.recurrentResponse && this.recurrentResponse.recurrent) {
              this.sendDetails.invoiceId = this.recurrentResponse.number
              this.sendDetails.recipients = this.recurrentResponse.emails
              if (this.recurrentResponse.invoice_message) {
                this.sendDetails.message = this.recurrentResponse.invoice_message
              }
            } else {
              // console.log('response.data.email ->', response.data)
              this.sendDetails.recipients = response.data.email
            }
          }

          if (this.storeSendDetails) {
            // recover previous stored sendDetails
            this.sendDetails.recipients = this.storeSendDetails.recipients
            this.sendDetails.subject = this.storeSendDetails.subject
            this.sendDetails.message = this.storeSendDetails.message
            this.sendDetails.attach_mollie_payment = this.storeSendDetails.attach_mollie_payment
            this.sendDetails.attach_ubl = this.storeSendDetails.attach_ubl
          }

          this.customerLoaded = true
        })


        

      }).catch((error) => {
        if (error.status == 404) {
          this.$router.push({ name: 'Invoices' })
        }
      })
      CostService.list({ invoice: invoice_id }).then((response) => {
        this.attachedCosts = response.data
      })
      HourService.count({ invoice: invoice_id }).then( (response) => {
        this.attachedHours = response.data
        this.attachedHoursReport.total = this.attachedHours
      })
    },
    updateMollieOnCompanySettings () {
      CompanySettingService.partialUpdate(this.currentCompany.companysettings.id, {
        'attach_mollie_payment': this.sendDetails.attach_mollie_payment
      }).then((response) => {
        // update stored company settings
        this.setCompanySettings(response.data)
      })
    },
    /**
     * Gets Dropzone Files from fileUploader component and sets this.invoiceFiles
     */
    updateInvoiceFiles () {
      this.invoiceFiles = this.$refs['file-uploader'].getDropzoneFiles()
    },
    /**
     * Alias for utils/niceBytes
     */
    niceBytes (size, base, unit = false) {
      return niceBytes(size, base, unit)
    },
    adjustAndSend (ev) {
      if (ev && ev.key && ev.target.nodeName !== 'TEXTAREA') {
        ev.preventDefault()
        return
      }
      if (!ev || (ev && !ev.key)) { // avoid submitting on key events
        this.sendDetails.recipients = this.recipientsFullString
        console.log('this.sendDetails.recipients', this.sendDetails.recipients)
        if (this.selectedDocuments.length > 0) {
          this.sendDetails.documents = this.selectedDocuments.map(doc => doc.id).join(',')
        }

        console.log('adjustAndSend ->', this.sendDetails)
        if (!this.sendDetails.recipients || this.sendDetails.recipients === '') {
          this.$store.commit('setAlert', {
            title: this.$gettext("Recipients is empty"),
            text: this.$gettext("You need input at least one valid email address."),
            variant:"warning",
            duration: 4
          })
        } else {
          this.showAdjustModal = true
          this.markedAsSent = false
        }
      }
    },
    sendInvoice (params) {
      console.log('sendInvoice -> true sending invoice', params)
      this.showAdjustModal = false
      if (this.sendDetails.attach_mollie_payment && this.mollieStatus !== 'ok') {
        this.showMollieError = true
      } else {
        this._sendInvoice(params.new_number, params.old_number)
      }
    },
    _sendInvoice (new_number, old_number) {
      if (new_number && old_number) {
        const regenerated_old_name = old_number + ' ' + this.$gettext('from') + ' ' + this.currentCompany.name
        if (!this.invoice.sender_company.is_premium || this.sendDetails.subject === regenerated_old_name) {
          this.sendDetails.subject = new_number + ' ' + this.$gettext('from') + ' ' + this.currentCompany.name
        }
        this.invoice.number = new_number
      }
      if (this.formIsValid()) {
        this.isSending = true
        if(!this.invoice.sender_company.is_premium && this.sendDetails.attach_hours) {
          this.sendDetails.attach_costs = false
          this.sendDetails.error = this.$gettext("You can't attach hours if you are not a premium member.")
        }
        InvoiceService.send(this.sendDetails).then(() => {
          window.mixpanel.track("Invoices: send",{
            'recipient': this.sendDetails.recipients,
            'invoice number': this.sendDetails.subject.split(' ')[0],
            'has_attached_documents': (this.sendDetails.documents) ? this.sendDetails.documents.length : '',
            'has_attached_costs': this.sendDetails.attach_costs,
            'has_attached_hours': this.sendDetails.attach_hours,
            'has_attached_ubl': this.sendDetails.attach_ubl
          })
          this.isSending = false
          this.$store.commit('setAlert', {
            title: this.$gettext('Invoice sent'),
            text: this.$gettext('Invoice sent successfully'),
            variant: 'primary',
            duration: 4
          })

          console.log('should update status to sent')
          this.setInvoiceStatus('s')

          this.$emit('invoice-updated')

          this.$router.push({ name: 'InvoiceView', params: { invoiceId: this.invoice.id } })

          delete this.sendDetails.errors
        }).catch((error) => {
          this.isSending = false
          if (error && error.status === 429) {
              this.$store.commit('setAlert', {
                  title: this.$gettext("Invoice not sent"),
                  text: this.$gettext("You need to wait before sending more invoices."),
                  variant:"warning",
                  duration: 4
              });
              if (!this.currentCompany.is_premium) {
                this.$store.commit('togglePremiumModal', 'web_email_throttle')
              }
          } else if (error && error.status === 500) {
            this.sendDetails.error = this.$gettext('There were some problems sending the invoice. Please try again later.')
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.sendDetails.error,
              variant: "danger",
              duration: 4
            })
          } else {
            this.sendDetails.errors = error.data
          }
          if (this.sendDetails.errors && this.sendDetails.errors.recipients) {
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.sendDetails.errors.recipients[0],
              variant: "danger",
              duration: 4
            })
          }
          console.error('Sending invoices error', { errors: error, details: this.sendDetails })
        })
      }
    },
    formIsValid () {
      return !(!this.sendDetails.recipients) && !(!this.sendDetails.subject)
    },
    documentSelected (item) {
      this.showDocumentSelector = false
      this.selectedDocuments.push(item)
    },
    removeDocumentFromList(data) {
      switch (data.item.key) {
        case 'ublfile':
          this.sendDetails.attach_ubl = false
          break;
        case 'costsreport':
          this.sendDetails.attach_costs = false
          break;
        case 'hoursreport':
          this.sendDetails.attach_hours = false
          break;
        default:
          this.selectedDocuments = this.selectedDocuments.filter((item) => item.id != data.item.id);
          this.$refs.document_selector.items.splice(0, 0, data.item);
          break;
      }
    },
    uploadAndAttach () {
      const description = this.$gettext('document uploaded for invoice %(name)s');
      const uploadData = {
        files: this.invoiceFiles,
        company_profile: this.currentCompany.id,
        description: description.replace("%(name)s", this.invoice.number)
      }
      if (this.invoiceFiles && this.invoiceFiles.length > 0) {
        this.uploadingFiles = true
        DocumentUploadService.bulkUpload(uploadData).then((response) => {
          this.uploadingFiles = false

          // evaluate each upload response status
          const recently_uploaded = response
          recently_uploaded.forEach(upload => {
            let showUploadError = false
            let error_text = ''
            switch (upload.status) {
              case 201:
                // add files to attachments
                this.documentSelected(upload.data)
                break;
              case 499:
                this.$store.commit('togglePremiumModal', 'web_documents_filelimit');
                break;
              case 400:
                showUploadError = true
                if (upload.data && upload.data.file) {
                  error_text = this.$gettext("No file was submitted.")
                } else if (upload.data && upload.data.non_field_errors) {
                  error_text = upload.data.non_field_errors
                } else {
                  error_text = this.$gettext("Error uploading document.")
                }
                break;
              default:
                showUploadError = true
                error_text = this.$gettext("Something went wrong...")
                break;
            }
            if (showUploadError) {
              this.$store.commit('setAlert', {
                title: this.$gettext("Error"),
                text: error_text,
                variant: "danger",
                duration: 4
              });
            }
          });

          // empty dropzone file list and invoiceFiles array
          this.$refs['file-uploader'].clear()
          this.invoiceFiles = null

          // hide file uploader
          this.showFileUploader = false
        });
      }
    },
    displayDocumentSelector () {
      this.showFileUploader = false
      this.showDocumentSelector = true
    },
    displayFileUploader () {
      this.showFileUploader = true
      this.showDocumentSelector = false
    },
    attachMolliePayment () {
      this.showMollieModal = false
      if (this.mollieStatus !== 'ok') {
        this.sendDetails.attach_mollie_payment = false
        this.showMollieError = true;
        switch (this.mollieStatus) {
          case 'no-profile':
            this.showMollieModal = 'no-profile'
            break;
          case 'missing-data':
            this.showMollieModal = 'missing-data'
            break;
          case 'not-premium':
            this.showMollieModal = false
            console.log('show premium modal')
            break;
        }
        return false
      }
    },
    detachMolliePayment () {
      this.sendDetails.attach_mollie_payment = false
    },
    updateCCSave () {
      CompanySettingService.partialUpdate(this.currentCompany.companysettings.id, {
        'include_sender_in_cc': this.updateCC
      }).then((response) => {
        // update stored company settings
        this.setCompanySettings(response.data)
      })
    }
  }
};
</script>

<style lang="scss" scoped>

</style>

<style lang="scss">
.invoice-send-wrap {

  .invoice-wrap-bg {
    .invoice-wrap {
      padding: 1rem;
    }
    @media (min-width: '992px') {
      background: $colorBgGrey;
      // background: #d6d6d6;
      padding: 1rem;
      min-height: 100%;
      .invoice-wrap {
        min-height: 100vh;
        max-width: 1200px;
        margin: 0 auto;
        border-radius: $radiusLg;
        background: white;
        box-shadow: $dropdownShadow;
        padding: 1rem;
      }
    }
  }
}

.invoice-send-form {
  padding-bottom: 2rem;
  .form-group {
    margin-bottom: 1.5rem;
  }
  .attachments-table-wrap {
    border: 1px solid #e9e9e9;
    border-radius: 12px;
    overflow: hidden;
    overflow-x: auto;
    .attachments-table {
      background: #f4f6f5; /* same as angular send table */
      thead {
        background: #fff;
        text-transform: none;
        color: $colorTextBase;
      }
      td:nth-child(1){ width: 40%; }
      td:nth-child(2){ width: 15%; }
      td:nth-child(3){ width: 40%; }
      td:nth-child(4){ width: 5%; }
    }
  }
  .document-selector-table-container {
    border: 1px solid $colorBorderDash;
    border-radius: $radiusLg;
    overflow: hidden;
    overflow-x: auto;
    .document-table {
      border-radius: $radiusLg;
    }
  }

}
</style>